import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { api } from "@/lib/api";
import { Users, ChevronDown, ChevronUp, Trophy, X, ExternalLink } from "lucide-react";

const LIVE_POLL_INTERVAL = 30_000; // 30 seconds

/** Formats seconds-ago into a short human label. */
function useTimeAgo(ts) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!ts) return;
    const id = setInterval(() => setNow(Date.now()), 5_000); // tick every 5s
    return () => clearInterval(id);
  }, [ts]);
  if (!ts) return "";
  const secs = Math.max(0, Math.round((now - ts) / 1000));
  if (secs < 10) return "just now";
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  return `${mins}m ago`;
}

export default function PlayHQPreview({ align = "center", hideCompleted = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("upcoming"); // "upcoming" | "completed"
  const [publishedGameIds, setPublishedGameIds] = useState(new Set());
  const [scaLogo, setScaLogo] = useState(null);
  const [openSheetId, setOpenSheetId] = useState(null);
  const pollRef = useRef(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Detect any live games to enable auto-refresh
  const hasLiveGames = useMemo(() => {
    if (!data) return false;
    const all = [...(data.next_games || []), ...(data.recent_results || [])];
    const now = Date.now();
    return all.some((g) => {
      if (g.status === "IN_PROGRESS") return true;
      // A game that has started but isn't final/completed is likely live
      const started = g.start_at && new Date(g.start_at).getTime() < now;
      return started && g.status !== "FINAL" && g.status !== "COMPLETED" && g.status !== "ABANDONED";
    });
  }, [data]);

  const timeAgo = useTimeAgo(hasLiveGames ? lastUpdated : null);

  // Fetch preview data (used for both initial load and poll refresh)
  const fetchData = useCallback(async (isRefresh = false) => {
    try {
      const r = await api.get("/playhq/public/preview");
      setData(r.data || null);
      setLastUpdated(Date.now());
    } catch { /* silent */ }
    finally { if (!isRefresh) setLoading(false); }
  }, []);

  // Initial load
  useEffect(() => {
    fetchData(false);
    // Also fetch published game IDs
    api.get("/fixtures/published-games").then(({ data: d }) => {
      setPublishedGameIds(new Set(d?.game_ids || []));
    }).catch(() => {});
    // Fetch SCA logo from landing affiliations
    api.get("/landing").then(({ data: d }) => {
      if (d?.affiliations) {
        const sca = d.affiliations.find(a => (a.name || "").toLowerCase().includes("southern cricket"));
        if (sca?.logo_url) setScaLogo(sca.logo_url);
      }
    }).catch(() => {});
  }, [fetchData]);

  // Auto-refresh polling when live games are in progress
  useEffect(() => {
    if (hasLiveGames) {
      pollRef.current = setInterval(() => fetchData(true), LIVE_POLL_INTERVAL);
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [hasLiveGames, fetchData]);

  const upcoming = data?.next_games || [];
  const completed = data?.recent_results || [];

  // Merge upcoming + recently completed so current-round cards persist
  // until every game in that round finishes.
  const allGames = useMemo(() => {
    const map = new Map();
    [...upcoming, ...completed].forEach((g) => { if (g.id && !map.has(g.id)) map.set(g.id, g); });
    return Array.from(map.values());
  }, [upcoming, completed]);

  const nextRoundGames = useMemo(() => {
    if (allGames.length === 0 && upcoming.length === 0) return [];

    // Find the earliest round that still has at least one non-completed game.
    const rounds = [...new Set(allGames.map((g) => g.round_name || ""))];
    let currentRound = null;
    for (const r of rounds) {
      const inRound = allGames.filter((g) => (g.round_name || "") === r);
      const hasIncomplete = inRound.some((g) => g.status !== "COMPLETED" && g.status !== "FINAL");
      if (hasIncomplete) { currentRound = r; break; }
    }

    // Show ALL games from the current round (the earliest round with
    // at least one incomplete game). Do NOT show games from future rounds
    // even if a grade has no game in the current round — the user must
    // see only the active round until every match in it completes.
    const display = new Map();
    if (currentRound !== null) {
      allGames.filter((g) => (g.round_name || "") === currentRound)
        .forEach((g) => display.set(g.id, g));
    }

    if (display.size === 0 && upcoming.length > 0) {
      // Fallback: show first upcoming round
      const firstRound = upcoming[0].round_name || "";
      upcoming.filter((g) => (g.round_name || "") === firstRound)
        .forEach((g) => display.set(g.id, g));
    }

    return Array.from(display.values());
  }, [allGames, upcoming]);

  const effectiveView = hideCompleted ? "upcoming" : view;
  const rows = effectiveView === "upcoming" ? nextRoundGames : completed;
  const roundLabel = effectiveView === "upcoming"
    ? "Upcoming"
    : "Completed";

  if (loading) return null;
  if (!data?.season || (upcoming.length === 0 && completed.length === 0)) return null;

  return (
    <section className="border-b border-border bg-[hsl(var(--bg))]" data-testid="landing-playhq-section">
      <div className={`px-5 sm:px-6 lg:px-8 pt-0 pb-6 sm:pb-8${align === "center" ? " mx-auto" : ""}`} style={{ maxWidth: 1920 }}>
        {/* Header + tabs */}
        <div className="flex items-end justify-between gap-3 flex-wrap mb-4 py-3 pl-5 sm:pl-6 lg:pl-[50px] pr-5 sm:pr-6 lg:pr-[232px] -mx-5 sm:-mx-6 lg:ml-[150px] lg:-mr-8 sticky top-16 z-10" style={{ background: "linear-gradient(135deg, #1E3A8A 0%, #2749C9 55%, #4169E1 100%)" }}>
          <h2
            className="text-4xl sm:text-5xl lg:text-6xl tracking-tight uppercase leading-none text-white"
            style={{ fontFamily: "'Barlow Condensed', 'Inter', sans-serif", fontWeight: 900 }}
          >
            Matches
          </h2>
          {!hideCompleted && (
          <div className="flex rounded-lg overflow-hidden border border-white/30 text-sm font-semibold" data-testid="playhq-toggle">
            <button
              onClick={() => setView("upcoming")}
              className={`px-3 sm:px-4 py-1.5 transition-colors ${
                view === "upcoming"
                  ? "bg-white text-[#1E3A8A] shadow-sm"
                  : "text-white/70 hover:text-white hover:bg-white/10"
              }`}
              data-testid="playhq-toggle-upcoming"
            >
              Upcoming
            </button>
            <button
              onClick={() => setView("completed")}
              className={`px-3 sm:px-4 py-1.5 transition-colors ${
                view === "completed"
                  ? "bg-white text-[#1E3A8A] shadow-sm"
                  : "text-white/70 hover:text-white hover:bg-white/10"
              }`}
              data-testid="playhq-toggle-completed"
              disabled={completed.length === 0}
            >
              Completed{completed.length === 0 ? " (0)" : ""}
            </button>
          </div>
          )}
        </div>

        {/* Live update badge */}
        {hasLiveGames && timeAgo && (
          <div className="flex items-center justify-center gap-2 mb-3 lg:ml-[200px] lg:mr-[200px]" data-testid="live-update-badge">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
            </span>
            <span className="text-[11px] text-muted-foreground">
              Live scores · updated {timeAgo}
            </span>
          </div>
        )}

        {/* Match rows */}
        {rows.length === 0 ? (
          <div className="-mx-5 sm:mx-0 lg:ml-[200px] lg:mr-[200px] rounded-none sm:rounded-b-lg border-x-0 sm:border-x border-y border-t-0 border-border bg-background p-8 text-center text-sm text-muted-foreground" data-testid="playhq-empty">
            {effectiveView === "upcoming" ? "No upcoming matches scheduled." : "No completed matches yet this season."}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 lg:ml-[200px] lg:mr-[200px]">
            {rows.map((g) => (
              <MatchRow
                key={g.id}
                game={g}
                completed={effectiveView === "completed"}
                hasPublishedTeam={publishedGameIds.has(g.playhq_id || g.id)}
                scaLogo={scaLogo}
                openSheetId={openSheetId}
                setOpenSheetId={setOpenSheetId}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function MatchRow({ game, completed, hasPublishedTeam, scaLogo, openSheetId, setOpenSheetId }) {
  const showTeamSheet = openSheetId === game.id;
  const [teamSheet, setTeamSheet] = useState(null);
  const [loadingSheet, setLoadingSheet] = useState(false);

  const dt = game.start_at ? new Date(game.start_at) : null;
  const dateLabel = dt ? dt.toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" }) : "";
  const timeLabel = dt ? dt.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }).toLowerCase().replace(/\s/g, "") : "";
  const venue = [game.venue, game.venue_suburb].filter(Boolean).join(", ");

  // Status detection — treat games that have started (start_at in the past)
  // but aren't FINAL as live, even if PlayHQ hasn't set IN_PROGRESS yet.
  // Also check pc_live flag from Play Cricket scraper.
  const hasStarted = dt && dt.getTime() < Date.now();
  const hasScores = !!(game.home?.score || game.away?.score);
  const isLive = game.pc_live || game.status === "IN_PROGRESS" || (hasStarted && !hasScores && game.status !== "FINAL" && game.status !== "COMPLETED" && game.status !== "ABANDONED" && !completed);

  // A game is effectively completed when it has scores and is no longer live
  const effectivelyCompleted = completed || (hasScores && !isLive);
  const scorecardUrl = game.pc_url || game.url || "";

  // Derive outcome: use PlayHQ's our_outcome if available, otherwise infer from scores
  let effectiveOutcome = game.our_outcome;
  if (!effectiveOutcome && effectivelyCompleted && hasScores) {
    // Parse scores to determine winner: "152" = all out, "3-155" = 3 wickets for 155
    const parseRuns = (s) => {
      if (!s) return null;
      const m = s.match(/(\d+)-(\d+)/);
      return m ? parseInt(m[2]) : parseInt(s);
    };
    const homeRuns = parseRuns(game.home?.score);
    const awayRuns = parseRuns(game.away?.score);
    if (homeRuns != null && awayRuns != null) {
      const clubIsHome = game.home?.is_club;
      const clubIsAway = game.away?.is_club;
      if (homeRuns > awayRuns) {
        effectiveOutcome = clubIsHome ? "WON" : clubIsAway ? "LOST" : null;
      } else if (awayRuns > homeRuns) {
        effectiveOutcome = clubIsAway ? "WON" : clubIsHome ? "LOST" : null;
      } else {
        effectiveOutcome = "DRAWN";
      }
    }
  }

  // Determine winner for completed matches
  const homeWon = effectivelyCompleted && ((effectiveOutcome === "WON" && game.home?.is_club) || (effectiveOutcome === "LOST" && game.away?.is_club));
  const awayWon = effectivelyCompleted && ((effectiveOutcome === "WON" && game.away?.is_club) || (effectiveOutcome === "LOST" && game.home?.is_club));

  const handleToggleTeamSheet = async () => {
    if (showTeamSheet) {
      setOpenSheetId(null);
      return;
    }
    if (!teamSheet) {
      setLoadingSheet(true);
      try {
        const { data } = await api.get(`/fixtures/team-sheet/${game.playhq_id || game.id}`);
        setTeamSheet(data);
      } catch {
        setTeamSheet(null);
      }
      setLoadingSheet(false);
    }
    setOpenSheetId(game.id);
  };

  // Live stats from backend
  const liveStats = game.live_stats;
  const hasLiveStats = isLive && liveStats && liveStats.innings && liveStats.innings.length > 0;

  // Map innings to home/away teams for score display
  const homeInnings = hasLiveStats
    ? liveStats.innings.find((inn) => inn.team_id === game.home?.team_id)
    : null;
  const awayInnings = hasLiveStats
    ? liveStats.innings.find((inn) => inn.team_id === game.away?.team_id)
    : null;

  return (
    <div className="space-y-3">
    <div
      className={`relative rounded-lg border border-border bg-[#faf8f5] shadow-sm hover:shadow-md hover:bg-[#f5f0e8] transition-all ${scorecardUrl ? "cursor-pointer" : ""}${isLive ? " ring-2 ring-emerald-500/50" : ""}`}
      data-testid={`playhq-match-${game.id}`}
      onClick={scorecardUrl ? () => window.open(scorecardUrl, "_blank", "noopener") : undefined}
      role={scorecardUrl ? "link" : undefined}
    >
      {/* Live pulse indicator */}
      {isLive && (
        <div className="absolute top-2 right-2 flex items-center gap-1.5 z-10">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
          </span>
          <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-600">Live</span>
        </div>
      )}

      <div className="flex">
        {/* Left accent card */}
        <div className="hidden sm:flex w-[112px] shrink-0 rounded-l-lg items-center justify-center overflow-hidden">
          {scaLogo && (
            <img src={scaLogo} alt="Southern Cricket Association" className="w-3/4 h-3/4 object-contain mix-blend-multiply" data-testid="playhq-sca-logo" />
          )}
        </div>
        <div className="flex-1 px-4 pt-4 pb-4 sm:pr-[128px]">
          {/* Date header */}
          {dt && (
            <div className="text-center text-xs sm:text-sm font-bold mb-1">
              {dateLabel}
            </div>
          )}

          {/* Team line */}
          <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 sm:gap-4">
            <div className="flex items-center justify-end gap-2 sm:gap-3">
              <div
                className={`text-right text-[clamp(1.1rem,3.5vw,1.75rem)] uppercase tracking-tight leading-[1.1] ${homeWon ? "text-foreground" : "text-gray-500"} ${game.home?.is_club ? "font-black" : "font-bold"}`}
                style={{ fontFamily: "'Barlow Condensed', 'Inter', sans-serif" }}
              >
                {homeWon && <Trophy className="h-4 w-4 sm:h-5 sm:w-5 text-amber-500 inline-block align-middle mr-1" />}
                {game.home?.name || "TBC"}
              </div>
              {game.home?.logo_url ? (
                <img src={game.home.logo_url} alt="" className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-contain shrink-0" />
              ) : (
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-muted shrink-0" />
              )}
            </div>

            {/* Centre: scores for live, or status badge */}
            <div className="flex flex-col items-center gap-0.5 sm:gap-1">
              {hasLiveStats ? (
                <div className="flex items-center gap-3 sm:gap-5">
                  <div className="text-center">
                    <div className="text-lg sm:text-2xl font-black tabular-nums text-[#1E3A8A]" style={{ fontFamily: "'Barlow Condensed', 'Inter', sans-serif" }}>
                      {homeInnings ? `${homeInnings.wickets != null ? homeInnings.wickets + "/" : ""}${homeInnings.runs}` : (game.home?.score || "—")}
                    </div>
                    {homeInnings?.overs ? (
                      <div className="text-[10px] sm:text-xs text-muted-foreground font-mono">({homeInnings.overs})</div>
                    ) : homeInnings?.wickets === 10 ? (
                      <div className="text-[10px] sm:text-xs text-muted-foreground font-mono">(all out)</div>
                    ) : null}
                  </div>
                  <div className="text-center">
                    <div className="text-lg sm:text-2xl font-black tabular-nums text-[#1E3A8A]" style={{ fontFamily: "'Barlow Condensed', 'Inter', sans-serif" }}>
                      {awayInnings ? `${awayInnings.wickets != null ? awayInnings.wickets + "/" : ""}${awayInnings.runs}` : (game.away?.score || "—")}
                    </div>
                    {awayInnings?.overs ? (
                      <div className="text-[10px] sm:text-xs text-muted-foreground font-mono">({awayInnings.overs})</div>
                    ) : awayInnings?.wickets === 10 ? (
                      <div className="text-[10px] sm:text-xs text-muted-foreground font-mono">(all out)</div>
                    ) : null}
                  </div>
                </div>
              ) : isLive && hasScores ? (
                /* Live game with scores from Play Cricket but no detailed innings */
                <div className="flex items-center gap-3 sm:gap-5">
                  <div className="text-lg sm:text-2xl font-black tabular-nums text-[#1E3A8A]" style={{ fontFamily: "'Barlow Condensed', 'Inter', sans-serif" }}>
                    {game.home?.score || "—"}
                  </div>
                  <div className="text-lg sm:text-2xl font-black tabular-nums text-[#1E3A8A]" style={{ fontFamily: "'Barlow Condensed', 'Inter', sans-serif" }}>
                    {game.away?.score || "—"}
                  </div>
                </div>
              ) : completed ? (
                <OutcomeBadge outcome={game.our_outcome} status={game.status} />
              ) : isLive ? (
                <span className="inline-block px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-md bg-emerald-600 text-[9px] sm:text-[10px] font-bold uppercase tracking-wider text-white">
                  In Progress
                </span>
              ) : hasScores ? (
                <OutcomeBadge outcome={effectiveOutcome} status={game.status} />
              ) : (
                <span className="inline-block px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-md bg-[hsl(var(--secondary))] text-[9px] sm:text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  Upcoming
                </span>
              )}
              {/* Result text from Play Cricket (e.g. "Derwent CC 2 leads by 3") */}
              {isLive && game.pc_result_text && (
                <span className="text-[10px] sm:text-xs font-semibold text-emerald-700 mt-0.5">{game.pc_result_text}</span>
              )}
              {timeLabel && !completed && !isLive && !hasScores && (
                <span className="text-[10px] sm:text-xs text-muted-foreground whitespace-nowrap">{timeLabel}</span>
              )}
              {/* Fallback score lines for completed games */}
              {!hasLiveStats && !isLive && game.home?.score && (
                <div className="text-xs sm:text-sm font-mono tabular-nums text-muted-foreground">{game.home.score}</div>
              )}
              {!hasLiveStats && !isLive && game.away?.score && (
                <div className="text-xs sm:text-sm font-mono tabular-nums text-muted-foreground">{game.away.score}</div>
              )}
            </div>

            <div className="flex items-center justify-start gap-2 sm:gap-3">
              {game.away?.logo_url ? (
                <img src={game.away.logo_url} alt="" className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-contain shrink-0" />
              ) : (
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-muted shrink-0" />
              )}
              <div
                className={`text-left text-[clamp(1.1rem,3.5vw,1.75rem)] uppercase tracking-tight leading-[1.1] ${awayWon ? "text-foreground" : "text-gray-500"} ${game.away?.is_club ? "font-black" : "font-bold"}`}
                style={{ fontFamily: "'Barlow Condensed', 'Inter', sans-serif" }}
              >
                {game.away?.name || "TBC"}
                {awayWon && <Trophy className="h-4 w-4 sm:h-5 sm:w-5 text-amber-500 inline-block align-middle ml-1" />}
              </div>
            </div>
          </div>

          {/* Footer — grade + venue + scorecard link (hidden when live) */}
          {!isLive && (
          <div className="mt-2 sm:mt-3 text-center text-[10px] sm:text-xs text-muted-foreground leading-tight">
            {game.grade_name && <span className="font-semibold text-foreground">{game.grade_name}</span>}
            {game.grade_name && (game.home?.name || game.away?.name) && <span> · </span>}
            <span>{game.home?.name} v {game.away?.name}</span>
            {venue && (
              <div className="mt-0.5">{venue}</div>
            )}
            {scorecardUrl && (hasScores || completed) && (
              <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-blue-600 font-medium">
                <ExternalLink className="h-3 w-3" />
                <span>View Full Scorecard</span>
              </div>
            )}
          </div>
          )}

        </div>
      </div>

      {/* Live match situation stats bar */}
      {hasLiveStats && liveStats.target != null && (
        <div className="border-t border-border bg-white/60 px-4 py-2.5 sm:px-6" data-testid="live-stats-bar">
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-y-2 gap-x-4 text-center">
            <LiveStatCell label="Current Run Rate" value={liveStats.current_run_rate?.toFixed(2) ?? "—"} />
            <LiveStatCell label="Required Run Rate" value={liveStats.required_run_rate?.toFixed(2) ?? "—"} />
            <LiveStatCell label="Runs to Win" value={liveStats.runs_to_win ?? "—"} />
            <LiveStatCell label="Balls Left" value={liveStats.balls_left ?? "—"} />
            <LiveStatCell label="Wickets Left" value={liveStats.wickets_left ?? "—"} />
            <LiveStatCell label="Target" value={liveStats.target ?? "—"} />
          </div>
        </div>
      )}
    </div>

    {/* Team Sheet — dialog overlay (same pattern as news detail) */}
    <Dialog open={hasPublishedTeam && showTeamSheet} onOpenChange={(v) => { if (!v) setOpenSheetId(null); }}>
      <DialogContent
        className="p-0 w-[85vw] max-w-[85vw] sm:w-[420px] sm:max-w-[420px] md:w-[460px] md:max-w-[460px] max-h-[98dvh] overflow-hidden rounded-xl border-0 shadow-2xl bg-transparent text-foreground [&>button]:hidden"
        data-testid={`team-sheet-panel-${game.id}`}
      >
        <DialogHeader className="sr-only">
          <DialogTitle>Team Sheet</DialogTitle>
        </DialogHeader>
        <div className="relative">
          <ScrollArea className="max-h-[90dvh]">
            {loadingSheet ? (
              <div className="text-center text-sm text-white/60 py-12" style={{ background: "#0c1d3d" }}>Loading team sheet…</div>
            ) : teamSheet ? (
              <TeamSheetCard teamSheet={teamSheet} />
            ) : (
              <div className="text-center text-sm text-white/60 py-12" style={{ background: "#0c1d3d" }}>Could not load team sheet.</div>
            )}
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
    </div>
  );
}

function TeamSheetCard({ teamSheet }) {
  const { players, grade, game_context } = teamSheet;
  const regulars = players.filter((p) => !p.emergency);
  const emergencies = players.filter((p) => p.emergency);

  // Parse grade label
  const gradeNum = grade;
  let gradeLabel = grade;
  if (gradeNum && /^\d+$/.test(gradeNum)) {
    const n = parseInt(gradeNum);
    const suffix = { 1: "st", 2: "nd", 3: "rd" }[n < 20 ? n : n % 10] || "th";
    gradeLabel = `${n}${suffix} Grade`;
  }

  // Parse date/time from game_context
  const ctx = game_context || {};
  const dt = ctx.start_at ? new Date(ctx.start_at) : null;
  const dayName = dt ? dt.toLocaleDateString(undefined, { weekday: "long" }).toUpperCase() : "";
  const dateStr = dt ? dt.toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" }) : "";
  const timeStr = dt ? dt.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }).toUpperCase() : "";

  // Track scroll position for chevron direction
  const scrollRef = React.useRef(null);
  const [atBottom, setAtBottom] = React.useState(false);
  const handleScroll = (e) => {
    const el = e.target;
    setAtBottom(el.scrollHeight - el.scrollTop - el.clientHeight < 10);
  };

  return (
    <div data-testid="team-sheet-card" className="relative overflow-hidden rounded-xl p-4" style={{ background: "linear-gradient(135deg, #1E3A8A 0%, #2749C9 55%, #4169E1 100%)" }}>
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full opacity-5 bg-white" />
        <div className="absolute -bottom-32 -left-16 w-80 h-80 rounded-full opacity-[0.03] bg-white" />
      </div>

      <div ref={scrollRef} onScroll={handleScroll} className="relative z-10 max-h-[75dvh] overflow-y-auto scrollbar-hide rounded-lg" style={{ scrollbarWidth: "none" }}>
        {/* Grade Title removed */}

        {/* VS Row removed */}

        {/* Day / Time / Location removed */}

        {/* Derwent CC header bar removed */}

        {/* Player list */}
        <div className="rounded-md bg-[#faf8f5] px-3 py-2">
          {regulars.map((p, i) => (
            <div
              key={p.user_id}
              className="flex items-stretch gap-3 pt-4"
              data-testid={`player-card-${p.user_id}`}
            >
              {p.avatar_url ? (
                <img
                  src={p.avatar_url}
                  alt=""
                  className="w-20 rounded-full object-cover ring-2 ring-black shrink-0 aspect-square self-center"
                />
              ) : (
                <div className="w-20 rounded-full bg-gray-200 flex items-center justify-center text-3xl font-bold text-gray-500 ring-2 ring-black shrink-0">
                  {(p.first_name?.[0] || "")}{(p.last_name?.[0] || "")}
                </div>
              )}
              <div className="flex-1 self-start">
                <div>
                  <div className="flex items-baseline gap-2">
                    <div
                      className="text-2xl font-bold uppercase text-black tracking-wide leading-none"
                      style={{ fontFamily: "'Barlow Condensed', 'Inter', sans-serif" }}
                    >
                      {p.first_name} {p.last_name}
                    </div>
                    {p.role === "captain" && (
                      <span className="text-[10px] font-black uppercase tracking-wider text-amber-600 bg-amber-500/10 px-1.5 py-0.5 rounded shrink-0">Captain</span>
                    )}
                    {p.role === "vice_captain" && (
                      <span className="text-[10px] font-black uppercase tracking-wider text-amber-600 bg-amber-500/10 px-1.5 py-0.5 rounded shrink-0">Vice Captain</span>
                    )}
                  </div>
                  <div className="mt-1 text-sm text-black/60 font-medium grid grid-cols-3 gap-x-4 gap-y-0.5">
                    <span>Debut: {p.debut || "—"}</span>
                    <span>Matches: {p.matches || "—"}</span>
                    <span>Runs: {p.runs || "—"}</span>
                    <span>HS: {p.hs || "—"}</span>
                    <span>Wickets: {p.wickets || "—"}</span>
                    <span>Catches: {p.catches || "—"}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Emergency players */}
        {emergencies.length > 0 && (
          <>
            <div className="mt-4 mb-2 py-1.5 rounded-md text-center" style={{ background: "linear-gradient(90deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.04) 100%)" }}>
              <div className="text-[10px] font-black uppercase tracking-[0.15em] text-white/50">Emergency Players</div>
            </div>
            <div className="space-y-1">
              {emergencies.map((p, i) => (
                <div
                  key={p.user_id}
                  className="flex items-center gap-3 rounded-lg px-3 py-2 bg-white/[0.03]"
                  data-testid={`player-card-${p.user_id}`}
                >
                  <div className="shrink-0 w-7 h-7 rounded bg-white/10 flex items-center justify-center text-xs font-black text-white/50 tabular-nums">
                    E{i + 1}
                  </div>
                  {p.avatar_url ? (
                    <img src={p.avatar_url} alt="" className="h-9 w-9 rounded-full object-cover object-top ring-1 ring-white/20 shrink-0" />
                  ) : (
                    <div className="h-9 w-9 rounded-full bg-white/10 flex items-center justify-center text-xs font-bold text-white/50 shrink-0">
                      {(p.first_name?.[0] || "")}{(p.last_name?.[0] || "")}
                    </div>
                  )}
                  <div
                    className="text-sm font-bold uppercase text-white/70 tracking-wide truncate"
                    style={{ fontFamily: "'Barlow Condensed', 'Inter', sans-serif" }}
                  >
                    {p.first_name} {p.last_name}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Footer removed */}
      </div>

      {/* Scroll indicator chevron — flips when at bottom */}
      <div className="absolute bottom-0 left-0 right-0 z-20 flex justify-center pointer-events-none pb-2">
        <div className="animate-bounce">
          {atBottom ? (
            <ChevronUp className="h-6 w-6 text-black" />
          ) : (
            <ChevronDown className="h-6 w-6 text-black" />
          )}
        </div>
      </div>
    </div>
  );
}

function PlayerCard({ player }) {
  const {
    first_name, last_name, avatar_url, role,
    debut, matches, runs, hs, catches, wickets,
  } = player;

  return (
    <div
      className="flex items-start gap-4 p-4 sm:p-4 rounded-xl bg-background border border-border"
      data-testid={`player-card-${player.user_id}`}
    >
      {/* Avatar + Role */}
      <div className="shrink-0 flex flex-col items-center">
        {avatar_url ? (
          <img
            src={avatar_url}
            alt={`${first_name} ${last_name}`}
            className="h-16 w-16 rounded-full object-cover object-top ring-2 ring-emerald-500/30"
          />
        ) : (
          <div className="h-16 w-16 rounded-full bg-[hsl(var(--secondary))] border border-border flex items-center justify-center text-lg font-bold text-muted-foreground">
            {(first_name?.[0] || "")}{(last_name?.[0] || "")}
          </div>
        )}
        {role === "captain" && (
          <span className="mt-1.5 text-[10px] sm:text-[9px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400 whitespace-nowrap">Captain</span>
        )}
        {role === "vice_captain" && (
          <span className="mt-1.5 text-[10px] sm:text-[9px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400 whitespace-nowrap">Vice Capt</span>
        )}
      </div>

      {/* Name + Stats */}
      <div className="flex-1 min-w-0">
        <div className="font-bold text-base leading-tight truncate">
          {first_name} {last_name}
        </div>

        <div className="mt-2 grid grid-cols-3 gap-x-4 gap-y-1.5">
          <StatItem label="Debut" value={debut || "—"} />
          <StatItem label="Matches" value={matches ?? "—"} />
          <StatItem label="Runs" value={runs ?? "—"} />
          <StatItem label="HS" value={hs ?? "—"} />
          <StatItem label="Catches" value={catches ?? "—"} />
          <StatItem label="Wickets" value={wickets ?? "—"} />
        </div>
      </div>
    </div>
  );
}

function StatItem({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold leading-tight whitespace-nowrap">{label}</div>
      <div className="text-sm font-bold tabular-nums leading-tight">{value}</div>
    </div>
  );
}

function OutcomeBadge({ outcome, status }) {
  if (status === "ABANDONED") return <Badge variant="outline" className="rounded-md border-amber-500/40 text-amber-700 bg-amber-500/10 text-[9px] sm:text-[10px] uppercase font-bold tracking-wider">Abandoned</Badge>;
  if (status === "FORFEIT") return <Badge variant="outline" className="rounded-md border-amber-500/40 text-amber-700 bg-amber-500/10 text-[9px] sm:text-[10px] uppercase font-bold tracking-wider">Forfeit</Badge>;
  if (outcome === "WON") return <Badge className="rounded-md bg-emerald-600 hover:bg-emerald-600 text-white text-[9px] sm:text-[10px] uppercase font-bold tracking-wider">Win</Badge>;
  if (outcome === "LOST") return <Badge className="rounded-md bg-red-600 hover:bg-red-600 text-white text-[9px] sm:text-[10px] uppercase font-bold tracking-wider">Loss</Badge>;
  if (outcome === "DRAWN") return <Badge variant="outline" className="rounded-md text-[9px] sm:text-[10px] uppercase font-bold tracking-wider">Draw</Badge>;
  return <Badge variant="outline" className="rounded-md text-[9px] sm:text-[10px] uppercase font-bold tracking-wider">Final</Badge>;
}

function LiveStatCell({ label, value }) {
  return (
    <div data-testid={`live-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-base sm:text-xl font-black tabular-nums text-[#1E3A8A]" style={{ fontFamily: "'Barlow Condensed', 'Inter', sans-serif" }}>
        {value}
      </div>
      <div className="text-[9px] sm:text-[10px] uppercase tracking-wider text-muted-foreground font-semibold leading-tight whitespace-nowrap">
        {label}
      </div>
    </div>
  );
}

